import React from "react";
import { Link } from "react-router-dom";

const AdditionalServices = () => {
  const services = [
    {
      title: "Carpet & Upholstery Cleaning",
      description:
        "Deep steam cleaning that removes stains, odors, and allergens from carpets and furniture",
      features: [
        "Hot water extraction",
        "Stain treatment",
        "Pet odor removal",
        "Fast drying",
      ],
      icon: "🧼",
      color: "from-cyan-400 to-cyan-600",
    },
    {
      title: "Window Cleaning",
      description:
        "Streak-free window cleaning for residential and commercial properties",
      features: [
        "Interior and exterior",
        "High-reach access",
        "Frame and sill cleaning",
        "Screen cleaning",
      ],
      icon: "🪟",
      color: "from-navy-700 to-navy-900",
    },
    {
      title: "Pressure Washing",
      description:
        "High-powered cleaning for driveways, patios, siding, and outdoor surfaces",
      features: [
        "Driveway cleaning",
        "Deck restoration",
        "Siding wash",
        "Concrete cleaning",
      ],
      icon: "💦",
      color: "from-cyan-500 to-navy-700",
    },
    {
      title: "Organizing & Decluttering",
      description:
        "Professional organizing services to create functional, clutter-free spaces",
      features: [
        "Space planning",
        "Donation coordination",
        "Storage solutions",
        "Ongoing maintenance",
      ],
      icon: "📦",
      color: "from-coral-400 to-coral-500",
    },
    {
      title: "Post-Construction Cleaning",
      description:
        "Thorough cleaning after renovations or construction projects",
      features: [
        "Dust and debris removal",
        "Paint splatter cleanup",
        "Final polish",
        "Move-in ready",
      ],
      icon: "🏗️",
      color: "from-navy-800 to-cyan-500",
    },
    {
      title: "Move-In/Move-Out Services",
      description: "Complete cleaning and preparation for seamless transitions",
      features: [
        "Deep cleaning",
        "Key exchange coordination",
        "Inspection support",
        "Rush service available",
      ],
      icon: "🚚",
      color: "from-cyan-400 to-navy-800",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-white via-gray-50 to-cyan-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-navy-900/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl text-center mx-auto">
            <div className="inline-block bg-cyan-400/10 border border-cyan-400/30 rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-bold text-navy-900 tracking-wide">
                SPECIALISED SOLUTIONS
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-navy-900 leading-none mb-6">
              Additional <span className="text-cyan-500">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Comprehensive cleaning and maintenance solutions for every need
            </p>
            <a
              href="#services"
              className="inline-block px-8 py-4 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 shadow-lg"
            >
              EXPLORE SERVICES
            </a>
          </div>
        </div>
      </section>

      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              <span className="text-cyan-500">Specialised</span> Cleaning
              Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional solutions for unique cleaning challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group relative">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl opacity-0 group-hover:opacity-10`}
                ></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-gray-200 group-hover:border-cyan-400 shadow-lg hover:shadow-2xl h-full flex flex-col">
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-3 group-hover:text-cyan-500">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                    {service.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <div
                        key={fIndex}
                        className="flex items-center text-sm text-gray-700"
                      >
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></span>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/get-quote"
                    className="block w-full py-3 bg-navy-900 text-white font-bold rounded-lg hover:bg-navy-800 text-center"
                  >
                    Get Quote
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                Bundle & <span className="text-cyan-400">Save</span>
              </h2>
              <p className="text-xl text-gray-300">
                Combine multiple services for better value and comprehensive
                care
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Complete Home Package",
                  desc: "Combine regular cleaning with carpet and window services",
                },
                {
                  title: "Move-Out Bundle",
                  desc: "End of tenancy + carpet cleaning + window washing",
                },
                {
                  title: "Property Refresh",
                  desc: "Pressure washing + window cleaning + organizing",
                },
              ].map((bundle, index) => (
                <div
                  key={index}
                  className="bg-navy-800/50 backdrop-blur-sm border border-cyan-400/30 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold text-cyan-400 mb-2">
                    {bundle.title}
                  </h3>
                  <p className="text-gray-300 text-sm">{bundle.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-cyan-400 to-cyan-600">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Ready for Specialised Service?
          </h2>
          <p className="text-xl text-navy-900 font-semibold mb-8 max-w-2xl mx-auto">
            Contact us to discuss your unique cleaning needs
          </p>
          <Link
            to="/get-quote"
            className="inline-block px-12 py-5 bg-navy-900 text-white font-black text-lg rounded-xl hover:bg-navy-800 shadow-2xl"
          >
            GET CUSTOM QUOTE
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AdditionalServices;
